import { ExistingDebtBuilder } from '~/builders/existing-debt/ExistingDebtBuilder';
import { getExistingDebtForYear } from '~/builders/existing-debt/ExistingDebtHelper';
import { TaxableValueBuilder } from '~/builders/taxable-value/TaxableValueBuilder';
import { applyMillageOverrides, hasTaxableValueOverrides } from '~/services/override-service';
import type {
  Candidate,
  Configuration,
  MillageInformation,
  Project,
  RevenueMap,
  TaxableValueMap,
  TaxValueInputs,
} from '~/types';
import { parseFormattedNumber } from '~/utils/numberParsing';

/**
 * RevenueMapBuilder - Builds revenue map from taxable value, millage shape, and existing debt
 *
 * The revenue map combines:
 * - Taxable value projections (from taxable-value builder)
 * - Millage rates (from millage processing)
 * - Existing debt service (from existing-debt builder)
 *
 * This creates a year-by-year view of total revenue, debt obligations, and available capacity.
 */
export class RevenueMapBuilder {
  /**
   * Initialize revenue map for a candidate
   */
  static buildRevenueMap(
    candidate: Candidate,
    project: Project,
    configuration: Configuration
  ): void {
    const { millageInformation } = candidate;
    const { millageMap: baseMillageMap } = millageInformation;
    const baseTaxableValueMap = project.taxableValueMap || {};
    const existingDebtProjects = project.existingDebtProjects || [];

    // Apply overrides from configuration (if present)
    // For taxable value: if there are growth rate overrides, we need to recalculate from scratch
    // because growth rate overrides have a cascading effect on all subsequent years
    const taxableValueMap = RevenueMapBuilder.resolveTaxableValueMap(
      project,
      configuration,
      baseTaxableValueMap
    );
    const millageMap = applyMillageOverrides(baseMillageMap, configuration.overrides?.millage);

    // Create combined debt service on-demand (it's not stored in project.existingDebtProjects)
    // This matches the pattern used in candidate-builder.ts
    const combinedDebtService =
      existingDebtProjects.length > 0
        ? ExistingDebtBuilder.combineDebtProjects(existingDebtProjects)
        : {
            id: 'combined',
            name: 'Combined Debt Service',
            existingDebtPaymentRecords: [],
          };

    const bondSeries = configuration.bondSeries;

    const revenueMap: RevenueMap = {};

    // Get existing debt service for each year
    const existingDebtPaymentRecords = combinedDebtService.existingDebtPaymentRecords || [];
    const dateRecordsMapByPaymentDate = project.dateElements?.dateRecordsMapByPaymentDate || {};

    // For each tax year in the millage map
    Object.entries(millageMap).forEach(([yearStr, millage]) => {
      const year = parseInt(yearStr, 10);
      const taxableValueEntry = taxableValueMap[year];

      if (!taxableValueEntry) {
        return; // Skip if no taxable value for this year
      }

      // Calculate revenue: (taxableValue * millage) / 1000 (millage is in mills)
      const revenue = Math.round((taxableValueEntry.taxableValue * millage) / 1000);

      // Get existing debt for this year using the payment date mapping
      const existingDebtForYear = getExistingDebtForYear(
        existingDebtPaymentRecords,
        dateRecordsMapByPaymentDate,
        year
      );

      const revenueMinusExistingDebt = revenue - existingDebtForYear;

      // Calculate years left to distribute (simplified)
      const lastYearOfMillageMap = Math.max(...Object.keys(millageMap).map(Number));
      const numberOfYearsLeftToDistribute = lastYearOfMillageMap - year + 1;

      revenueMap[year] = {
        taxYear: year,
        growthRate: taxableValueEntry.growthRate,
        taxableValue: taxableValueEntry.taxableValue,
        millage: millage,
        revenue: revenue,
        existingDebt: existingDebtForYear,
        revenueMinusExistingDebt: revenueMinusExistingDebt,
        bondSeriesDetailsByBondId: bondSeries.reduce(
          (acc, bond) => {
            // Determine if bond is active in this tax year based on issue date
            const bondIssueYear = new Date(bond.issueDate).getFullYear();
            const isActive = year >= bondIssueYear;

            acc[bond.id] = {
              isActive: isActive,
              accumulatedInterest: 0,
            };
            return acc;
          },
          {} as { [key: string]: { isActive: boolean; accumulatedInterest: number } }
        ),
        totalAccumulatedInterest: 0,
        availableRevenue: revenueMinusExistingDebt,
        revenueCapacity: Math.round(revenueMinusExistingDebt / numberOfYearsLeftToDistribute),
        isTunable: true, // Simplified for demo
      };
    });

    candidate.revenueMap = revenueMap;
  }

  /**
   * Build revenue map from component parts (without full candidate)
   * Note: This method requires project and configuration since Candidate no longer stores baseProjectElements, drfInformation, or bondSeries
   */
  static buildFromComponents(
    project: Project,
    configuration: Configuration,
    millageInformation: MillageInformation
  ): RevenueMap {
    // Create a minimal candidate structure for the builder
    const tempCandidate: Candidate = {
      id: 'temp',
      name: 'temp',
      description: 'temp',
      isProcessed: false,
      createdInPhase: 'first-fit',
      millageInformation,
      revenueMap: {},
      cashFlows: [],
      distributionStrategy: 'unlimited',
      results: {
        isViable: true,
        failureMessages: [],
        totalRevenue: 0,
        totalInterest: 0,
        totalPrincipal: 0,
        revenueUtilization: 0,
        interestRatio: 0,
        efficiency: 0,
        bondSeriesResults: [],
      },
    };

    RevenueMapBuilder.buildRevenueMap(tempCandidate, project, configuration);
    return tempCandidate.revenueMap;
  }

  /**
   * Resolve taxable value map with overrides applied
   *
   * Growth rate overrides require recalculating the entire projection from scratch
   * because changing a year's growth rate cascades to all subsequent years.
   */
  private static resolveTaxableValueMap(
    project: Project,
    configuration: Configuration,
    baseTaxableValueMap: TaxableValueMap
  ): TaxableValueMap {
    // If no taxable value overrides, use the base map
    if (!hasTaxableValueOverrides(configuration.overrides)) {
      return baseTaxableValueMap;
    }

    // We have growth rate overrides - need to recalculate from scratch
    // This requires the original tax value inputs from the project
    if (!project.taxValueInputs) {
      // No inputs available to recalculate - fall back to base map
      return baseTaxableValueMap;
    }

    // Convert form inputs (strings) to engine inputs (numbers)
    const taxValueInputs: TaxValueInputs = {
      startingYearTaxableValue: parseFormattedNumber(project.taxValueInputs.initialTaxableValue),
      startingYearGrowthRate: Number.parseFloat(project.taxValueInputs.startYearGrowthRate) / 100,
      fiveYearRate: Number.parseFloat(project.taxValueInputs.fiveYearGrowthRate) / 100,
      twentyYearRate: Number.parseFloat(project.taxValueInputs.twentyYearGrowthRate) / 100,
    };

    // Get starting tax year from the base map
    const taxYears = Object.keys(baseTaxableValueMap)
      .map(Number)
      .sort((a, b) => a - b);
    if (taxYears.length === 0) {
      return baseTaxableValueMap;
    }
    const startingTaxYear = taxYears[0];
    const yearsToProject = taxYears.length;

    // Rebuild taxable value map with growth rate overrides
    const rebuiltEntries = TaxableValueBuilder.buildWithFixedYears(
      taxValueInputs,
      startingTaxYear,
      yearsToProject,
      configuration.overrides?.taxableValue
    );

    // Convert array to map format
    const result: TaxableValueMap = {};
    for (const entry of rebuiltEntries) {
      result[entry.taxYear] = entry;
    }

    return result;
  }
}
